import { colors } from "@constants/theme";
import React, { useRef } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Cross from "@assets/svg/CrossIcon";
import {
  StyleSheet,
  View,
  Modal,
  TouchableWithoutFeedback,
  TouchableOpacity,
  FlatList,
  ScrollView,
} from "react-native";
import Button from "@components/Button";
import Text from "@components/CustomText";
import ColumnDatePicker from "./ColumnDatePicker";

export interface FilterCategory {
  key: string;
  label: string;
  type?: "pills" | "date";
}

export interface FilterOption {
  title: string;
  type?: "pills" | "date";
  values?: any[];
  startDate?: string;
  endDate?: string;
  key?: string;
}

export interface FilterData {
  title: string;
  categories: FilterCategory[];
  options: Record<string, FilterOption>;
  submit_text: string;
  clear_text: string;
}

interface FilterModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: () => void;
  filterData: FilterData;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  handleOptionPress: (item: any) => void;
  getSelectedFilters: () => any;
  clearFilters: () => void;
  handleDateChange?: (type: "start" | "end", date: string) => void;
}

const FilterPills: React.FC<{
  data?: FilterOption;
  selectedFilter: any;
  setSelectedFilter: (filter: any) => void;
}> = ({ data, selectedFilter, setSelectedFilter }) => {
  return (
    <ScrollView
      style={styles.scrollViewContainer}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.pillsContainer}>
        {data?.values?.map((item: any, index: number) => (
          <TouchableOpacity
            key={index}
            style={[
              styles.pill,
              selectedFilter?.includes(item.key) && styles.selectedPill,
            ]}
            onPress={() => setSelectedFilter(item.key)}
          >
            <Text
              variant="body_text_3"
              style={
                selectedFilter?.includes(item.key) && styles.selectedPillText
              }
            >
              {item.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );
};

const DateFilter: React.FC<{
  data?: FilterOption;
  onDateChange: (type: "start" | "end", date: string) => void;
}> = ({ data, onDateChange }) => {
  const dateRef = useRef();

  const handleDateChanged = (dateType: { type: string }, date: string) => {
    onDateChange(dateType.type === "fromDate" ? "start" : "end", date);
  };

  return (
    <View style={styles.dateFilterContainer}>
      <ColumnDatePicker
        ref={dateRef}
        dates={{ fromDate: data?.startDate, toDate: data?.endDate }}
        handleDateChanged={handleDateChanged}
      />
    </View>
  );
};

const FilterModal: React.FC<FilterModalProps> = ({
  visible,
  onClose,
  onSave,
  filterData,
  selectedCategory,
  setSelectedCategory,
  handleOptionPress,
  getSelectedFilters,
  clearFilters,
  handleDateChange,
}) => {
  const insets = useSafeAreaInsets();
  const renderCategory = ({ item }: { item: FilterCategory }) => (
    <TouchableOpacity
      style={[
        styles.categoryButton,
        selectedCategory === item.key && styles.activeCategory,
      ]}
      onPress={() => setSelectedCategory(item.key)}
      testID={`${item.key}+${item.label}`}
    >
      <Text
        variant={selectedCategory === item.key ? "body_text_2" : "body_text_3"}
        style={selectedCategory === item.key ? styles.activeCategoryText : {}}
      >
        {item.label}
      </Text>
    </TouchableOpacity>
  );

  const renderFilterContent = () => {
    const currentOption = filterData?.options?.[selectedCategory];
    const filterType = currentOption?.type || "pills";

    if (filterType === "date" && handleDateChange) {
      return (
        <DateFilter data={currentOption} onDateChange={handleDateChange} />
      );
    }

    return (
      <FilterPills
        data={currentOption}
        selectedFilter={getSelectedFilters()}
        setSelectedFilter={handleOptionPress}
      />
    );
  };

  return (
    <Modal
      statusBarTranslucent
      navigationBarTranslucent
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={onClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.modalBackground} />
      </TouchableWithoutFeedback>
      <View style={styles.contentContainer}>
        <View style={styles.headingContainer}>
          <Text variant="title_20">{filterData?.title}</Text>
        </View>
        <View style={styles.modalCross}>
          <TouchableWithoutFeedback onPress={onClose}>
            <Cross width={18} height={18} />
          </TouchableWithoutFeedback>
        </View>
        <View style={styles.scrollContainer}>
          <FlatList
            data={filterData?.categories}
            renderItem={renderCategory}
            keyExtractor={(item) => item.key}
            style={styles.categoryList}
          />
          {renderFilterContent()}
        </View>
        <View style={[styles.spacer, { height: 60 + insets.bottom }]} />
        <View
          style={[
            styles.buttonContainer,
            { paddingBottom: 30 + insets.bottom },
          ]}
        >
          <View style={styles.buttonWrapper}>
            <Button
              borderRadius={8}
              btnText={filterData?.clear_text}
              buttonWidth="100%"
              height={42}
              shouldBoldText
              buttonFontType="head3"
              buttonColor={colors.white}
              textColor={colors.black}
              borderColor={colors.black02}
              onPress={clearFilters}
            />
          </View>
          <View style={styles.buttonWrapper}>
            <Button
              borderRadius={8}
              btnText={filterData?.submit_text}
              buttonWidth="100%"
              height={42}
              shouldBoldText
              buttonFontType="head3"
              buttonColor={"rgba(25, 129, 85, 1)"}
              textColor={colors.white}
              borderColor={"rgba(25, 129, 85, 1)"}
              onPress={onSave}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalBackground: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  contentContainer: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    backgroundColor: colors.white,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingTop: 16,
    maxHeight: "80%",
    minHeight: "60%",
  },
  modalCross: {
    position: "absolute",
    right: 10,
    top: -35,
    backgroundColor: colors.white,
    borderRadius: 50,
    zIndex: 1,
    padding: 6,
  },
  headingContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingBottom: 12,
    borderBottomColor: colors.black02,
    borderBottomWidth: 0.5,
  },
  scrollContainer: {
    flexDirection: "row",
    minHeight: "50%",
  },
  categoryList: {
    width: "30%",
    backgroundColor: "rgba(245, 245, 245, 1)",
  },
  categoryButton: {
    padding: 12,
    paddingLeft: 16,
  },
  activeCategory: {
    backgroundColor: colors.white,
  },
  activeCategoryText: {
    color: colors.mediumBlack,
  },
  pill: {
    backgroundColor: colors.white,
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    margin: 4,
    borderWidth: 1,
    borderColor: colors.black02,
    marginBottom: 8,
  },
  selectedPill: {
    backgroundColor: "rgba(25, 129, 85, 1)",
    borderColor: "rgba(25, 129, 85, 1)",
  },
  selectedPillText: {
    color: colors.white,
  },
  dateFilterContainer: {
    padding: 16,
    width: "70%",
  },
  spacer: {
    height: 80,
  },
  buttonContainer: {
    paddingHorizontal: 10,
    flexDirection: "row",
    paddingVertical: 30,
    justifyContent: "space-evenly",
    borderTopColor: colors.black02,
    borderTopWidth: 0.5,
    bottom: 0,
    position: "absolute",
    backgroundColor: colors.white,
    width: "100%",
  },
  buttonWrapper: {
    flex: 1,
    marginHorizontal: 8,
  },
  pillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    paddingBottom: 180,
  },
  scrollViewContainer: {
    width: "70%",
    padding: 10,
  },
});

export default FilterModal;
